import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTheme } from "next-themes";
import { ShieldAlert, Siren } from "lucide-react";

import { NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";
import { cn } from "@/lib/utils";

type AdminProfileSummary = {
  id: string;
  displayName: string;
  handle: string;
  photoUrl: string | null;
};

type AdminReportItem = {
  index: number;
  id: string;
  type?: "post" | "user";
  reason: string;
  details: string | null;
  createdAt: string;
  reporter: AdminProfileSummary;
  author: AdminProfileSummary;
  post: {
    id: string;
    body: string;
    createdAt: string;
    postType: "standard" | "affirmation";
  };
  isAuthorBanned: boolean;
};

type BannedUserItem = {
  userId: string;
  profile: AdminProfileSummary;
  reason: string;
  bannedAt: string | null;
};

async function fetchReports() {
  const res = await adminFetch("/api/admin/reports");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Şikayet listesi alınamadı.");
  }

  return (Array.isArray(json.items) ? json.items : []) as AdminReportItem[];
}

async function closeReport(index: number) {
  const res = await adminFetch(`/api/admin/reports/${index}`, { method: "DELETE" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Şikayet kapatılamadı.");
  }
}

async function deletePost(postId: string) {
  const res = await adminFetch(`/api/admin/posts/${encodeURIComponent(postId)}`, { method: "DELETE" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Post silinemedi.");
  }
}

async function banUser(input: { userId: string; reason: string }) {
  const res = await adminFetch(`/api/admin/users/${encodeURIComponent(input.userId)}/ban`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason: input.reason }),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Kullanıcı yasaklanamadı.");
  }
}

async function unbanUser(userId: string) {
  const res = await adminFetch(`/api/admin/users/${encodeURIComponent(userId)}/ban`, { method: "DELETE" });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Yasak kaldırılamadı.");
  }
}

async function fetchBannedUsers() {
  const res = await adminFetch("/api/admin/users/banned");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok || !json?.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Yasaklı kullanıcı listesi alınamadı.");
  }

  return (Array.isArray(json.items) ? json.items : []) as BannedUserItem[];
}

function formatDate(value?: string | null) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("tr-TR", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

export default function AdminNumerolojiReports() {
  const queryClient = useQueryClient();
  const { sessionQuery, loginMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const reportsQuery = useQuery({
    queryKey: ["admin-social-reports"],
    queryFn: fetchReports,
    enabled: configured && authenticated,
    staleTime: 2_500,
  });

  const bannedUsersQuery = useQuery({
    queryKey: ["admin-social-banned-users"],
    queryFn: fetchBannedUsers,
    enabled: configured && authenticated,
    staleTime: 5_000,
  });

  const closeMutation = useMutation({
    mutationFn: closeReport,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-social-reports"] });
    },
  });

  const deletePostMutation = useMutation({
    mutationFn: deletePost,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-social-reports"] });
    },
  });

  const banMutation = useMutation({
    mutationFn: banUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-social-reports"] });
      await queryClient.invalidateQueries({ queryKey: ["admin-social-banned-users"] });
    },
  });

  const unbanMutation = useMutation({
    mutationFn: unbanUser,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-social-reports"] });
      await queryClient.invalidateQueries({ queryKey: ["admin-social-banned-users"] });
    },
  });

  if (!configured) {
    return (
      <div className="mx-auto max-w-2xl py-16">
        <Card className="p-6">
          <div className="flex items-start gap-3">
            <ShieldAlert className="mt-0.5 h-5 w-5 text-amber-500" />
            <div>
              <div className="text-base font-semibold">Admin girişi ayarlı değil</div>
              <div className="mt-1 text-sm text-muted-foreground">Sunucu tarafında admin girişi ayarlanmalı.</div>
              <div className="mt-4">
                <Button asChild className="rounded-full">
                  <Link to="/admin/numeroloji">Admin panel</Link>
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (!authenticated) {
    return (
      <div className="mx-auto max-w-2xl py-16">
        <AdminLoginCard
          configured={configured}
          onLogin={(args) => loginMutation.mutate(args)}
          isLoading={loginMutation.isPending}
          error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <main className="mx-auto w-full max-w-[1500px] flex-1 p-4 md:p-6">
          <div className="mb-4 flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-200">
              <Siren className="h-5 w-5" />
            </span>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Şikayet Yönetimi</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">Akış paylaşımlarındaki şikayetleri yönetebilir, post silebilir ve kullanıcı yasaklayabilirsin.</p>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-[1.5fr_1fr]">
            <Card className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Açık Şikayetler</h2>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                  {(reportsQuery.data ?? []).length} kayıt
                </span>
              </div>

              {reportsQuery.isLoading ? <p className="text-sm text-slate-500">Şikayetler yükleniyor...</p> : null}
              {reportsQuery.isError ? <p className="text-sm text-rose-600">{(reportsQuery.error as Error).message}</p> : null}

              {!reportsQuery.isLoading && !(reportsQuery.data ?? []).length ? (
                <p className="text-sm text-slate-500">Henüz şikayet kaydı yok.</p>
              ) : null}

              <div className="space-y-3">
                {(reportsQuery.data ?? []).map((item) => (
                  <div key={`${item.id}-${item.index}`} className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900/70">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-semibold">
                        {item.reporter.displayName || "Kullanıcı"} → {item.author.displayName || "Kullanıcı"}
                      </div>
                      <div className="flex items-center gap-2">
                        {item.type === "user" ? (
                          <span className="rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-700 dark:bg-amber-500/20 dark:text-amber-200">Kullanıcı şikayeti</span>
                        ) : null}
                        <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                          {formatDate(item.createdAt)}
                        </span>
                        <span
                          className={cn(
                            "rounded-full px-2 py-1 text-[11px] font-semibold",
                            item.isAuthorBanned ? "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200",
                          )}
                        >
                          {item.isAuthorBanned ? "Yasaklı" : "Aktif"}
                        </span>
                      </div>
                    </div>

                    <p className="mt-2 text-xs font-semibold text-slate-600 dark:text-slate-300">Sebep: {item.reason}</p>
                    {item.details ? <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Detay: {item.details}</p> : null}
                    {item.type !== "user" ? (
                      <p className="mt-2 line-clamp-2 text-sm text-slate-700 dark:text-slate-200">{item.post.body || "(Post silinmiş veya içerik boş)"}</p>
                    ) : null}

                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" variant="outline" className="h-8 rounded-full px-3 text-xs" disabled={closeMutation.isPending} onClick={() => closeMutation.mutate(item.index)}>
                        Şikayeti Kapat
                      </Button>

                      {item.type !== "user" ? (
                        <Button type="button" variant="outline" className="h-8 rounded-full px-3 text-xs" disabled={deletePostMutation.isPending || !item.post.id} onClick={() => deletePostMutation.mutate(item.post.id)}>
                          Postu Sil
                        </Button>
                      ) : null}

                      {item.isAuthorBanned ? (
                        <Button type="button" variant="outline" className="h-8 rounded-full px-3 text-xs" disabled={unbanMutation.isPending || !item.author.id} onClick={() => unbanMutation.mutate(item.author.id)}>
                          Yasağı Kaldır
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          className="h-8 rounded-full px-3 text-xs"
                          disabled={banMutation.isPending || !item.author.id}
                          onClick={() => {
                            const reason = window.prompt("Yasak sebebi", item.reason || "Uygunsuz içerik")?.trim();
                            if (!reason) return;
                            banMutation.mutate({ userId: item.author.id, reason });
                          }}
                        >
                          Kullanıcıyı Yasakla
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-lg font-semibold">Yasaklı Kullanıcılar</h2>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-200">
                  {(bannedUsersQuery.data ?? []).length}
                </span>
              </div>

              {bannedUsersQuery.isLoading ? <p className="text-sm text-slate-500">Yasak listesi yükleniyor...</p> : null}
              {bannedUsersQuery.isError ? <p className="text-sm text-rose-600">{(bannedUsersQuery.error as Error).message}</p> : null}

              {!bannedUsersQuery.isLoading && !(bannedUsersQuery.data ?? []).length ? (
                <p className="text-sm text-slate-500">Yasaklı kullanıcı bulunmuyor.</p>
              ) : null}

              <div className="space-y-2">
                {(bannedUsersQuery.data ?? []).map((item) => (
                  <div key={item.userId} className="rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900/70">
                    <p className="text-sm font-semibold">{item.profile.displayName || "Kullanıcı"}</p>
                    <p className="text-xs text-slate-500">@{item.profile.handle || item.userId.slice(0, 8)}</p>
                    <p className="mt-1 text-xs text-slate-500">Sebep: {item.reason}</p>
                    <p className="text-xs text-slate-500">Tarih: {formatDate(item.bannedAt)}</p>
                    <div className="mt-2">
                      <Button type="button" variant="outline" className="h-8 rounded-full px-3 text-xs" disabled={unbanMutation.isPending} onClick={() => unbanMutation.mutate(item.userId)}>
                        Yasağı Kaldır
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {closeMutation.isError ? <p className="mt-3 text-sm text-rose-600">{(closeMutation.error as Error).message}</p> : null}
          {deletePostMutation.isError ? <p className="mt-2 text-sm text-rose-600">{(deletePostMutation.error as Error).message}</p> : null}
          {banMutation.isError ? <p className="mt-2 text-sm text-rose-600">{(banMutation.error as Error).message}</p> : null}
          {unbanMutation.isError ? <p className="mt-2 text-sm text-rose-600">{(unbanMutation.error as Error).message}</p> : null}
        </main>
      </div>
    </div>
  );
}
